/**
 * Campaign click-map report (#32). Exported for `Report.test.tsx` (#253).
 */
import { useEffect, useState } from "react";
import { useAsync } from "../useAsync.js";
import { NewsletterSend } from "../NewsletterSend.js";
import { Kpi } from "../Kpi.js";
import { can, type Grant } from "../rbac.js";
import { api, type CampaignReport, type SeriesReport } from "../api.js";
import { AutoOpensNote } from "../AutoOpensNote.js";
import { CampaignPicker } from "../CampaignPicker.js";
import { meanSendRates } from "@addressium/domain";

export function Report({
  org,
  grant,
  initialCampaign,
}: {
  org: string;
  grant: Grant | null;
  /** Preselected when arriving from a link elsewhere (e.g. a Schedules row). */
  initialCampaign?: string;
}) {
  const campaigns = useAsync(() => api.campaigns(org), [org]);
  const series = useAsync(() => api.series(org), [org]);
  const [campaign, setCampaign] = useState(initialCampaign ?? "");
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [err, setErr] = useState("");
  // A recurring series is reported per EDITION, not as one campaign: each
  // firing has its own campaignId and its own counters, so a bad send is
  // visible instead of being averaged into the series total.
  const [seriesId, setSeriesId] = useState("");
  const [seriesReport, setSeriesReport] = useState<SeriesReport | null>(null);
  const [from, setFrom] = useState("");
  const [through, setThrough] = useState("");

  const loadSeries = async (id: string = seriesId) => {
    setErr(""); setSeriesReport(null);
    try {
      setSeriesReport(await api.seriesReport(org, id));
    } catch (e) {
      setErr(String(e));
    }
  };

  /**
   * Filter editions to the chosen window, client-side.
   *
   * The edition id is `<series>-<editionKey>` and the key is the firing
   * timestamp, so a lexical compare on the suffix IS a chronological one. The
   * route returns every edition; when a daily series has years of history this
   * should move server-side, but filtering what we already fetched is honest
   * for now and needs no API change.
   */
  const editionsInRange = (seriesReport?.editions ?? []).filter((e) => {
    if (!from && !through) return true;
    const key = e.createdAt ? e.createdAt.slice(0,10).replace(/-/g, "") : e.campaignId.slice(seriesId.length + 1).replace(/-/g, "");
    if (from && key < from.replace(/-/g, "")) return false;
    if (through && key > `${through.replace(/-/g, "")}\uffff`) return false;
    return true;
  });

  /**
   * Counts over the window are SUMMED; open and click rates are the AVERAGE OF
   * EACH EDITION'S OWN RATE (#369).
   *
   * This reverses the earlier summed-counter rate, by the product owner's call
   * on #369: an "average open rate" is the mean of each send's opens ÷
   * delivered, the same figure the Dashboard and Analytics show, so a series
   * reads one number everywhere. An edition with nothing delivered has no rate
   * and is left out rather than averaged in as 0% (`meanSendRates`).
   *
   * The bounce rate stays summed bounces ÷ summed sent — the base the halt gate
   * and the campaign report use.
   */
  const rangeTotals = editionsInRange.reduce(
    (acc, e) => {
      for (const k of Object.keys(e.counters) as Array<keyof typeof e.counters>) {
        acc[k] = (acc[k] ?? 0) + (e.counters[k] ?? 0);
      }
      return acc;
    },
    {} as Record<string, number>,
  );
  const rangeRates = meanSendRates(editionsInRange.map((e) => e.counters));
  const meanRate = (r: number | undefined) => (r === undefined ? "—" : pct(r));
  const bounceRate = rangeTotals.sent ? pct((rangeTotals.bounces ?? 0) / rangeTotals.sent) : "—";

  const load = async (id: string = campaign) => {
    setErr(""); setReport(null);
    try {
      setReport(await api.report(org, id));
    } catch (e) {
      setErr(String(e));
    }
  };

  /**
   * Series parents, by id (#348).
   *
   * A recurring parent never accumulates: every edition sends under its own
   * `<series>-<editionKey>` id with its own record, and nothing rolls up to the
   * parent. Its campaign report is therefore all zeros however many editions
   * have gone out — which reads as a dead list or a broken pipeline (#221).
   */
  const seriesIds = new Set((series.data ?? []).map((s) => s.seriesId));

  // Arriving from a link means the operator already chose a campaign; making
  // them press Load again would be asking the same question twice. Keyed on the
  // id so navigating from one row to another re-reads rather than showing the
  // previous campaign's report under the new name.
  //
  // Waits for the series list (#348): a Schedules row for a recurring series
  // carries the SERIES id, and requesting its campaign report first would flash
  // the parent's zeros. If the series read fails we fall back to the campaign
  // report — the old behaviour — rather than showing nothing at all.
  useEffect(() => {
    if (!initialCampaign || series.loading) return;
    if (seriesIds.has(initialCampaign)) {
      setCampaign("");
      setReport(null);
      setSeriesId(initialCampaign);
      void loadSeries(initialCampaign);
      return;
    }
    setCampaign(initialCampaign);
    void load(initialCampaign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCampaign, org, series.loading]);

  const maxClicks = report ? Math.max(1, ...report.clickMap.rows.map((r) => r.clicks)) : 1;
  return (
    <div>
      <h1 className="h1">Campaign report</h1>
      {/* Series parents are left out (#348): their report is always zero.
          Their numbers are per edition, under "Recurring series" below. */}
      <CampaignPicker
        campaigns={(campaigns.data ?? []).filter((c) => !seriesIds.has(c.campaignId))}
        loading={campaigns.loading}
        selected={campaign}
        onPick={(id) => { setCampaign(id); void load(id); }}
      />
      {err && <p className="err">{err}</p>}
      {report && (
        <>
          <div className="card">
            <div className="kpis">
              <Kpi n={report.counters.sent} l="sent" />
              {/* `delivered` is the denominator an operator actually reasons
                  about — "sent" is what SES accepted, not what arrived — and it
                  was computed, returned and then discarded here (#253). */}
              <Kpi n={report.counters.delivered} l="delivered" />
              {/* Each rate names its base (#369): engagement is of what
                  arrived, while bounces and complaints stay of sent because
                  that is what the halt gate measures. */}
              {/* "—" on a zero base, as Analytics' `pctOf` does: the API
                  divides by 1 there, so a send still in flight would read
                  "0.0%", or opens ÷ 1 when opens land before deliveries. */}
              <Kpi n={report.counters.opens} l={`opens (${rateOf(report.rates.openRate, report.counters.delivered)} of delivered)`} />
              <Kpi n={report.counters.clicks} l={`clicks (${rateOf(report.rates.clickRate, report.counters.delivered)} of delivered)`} />
              <Kpi n={report.counters.bounces} l={`bounces (${rateOf(report.rates.bounceRate, report.counters.sent)} of sent)`} />
              <Kpi n={report.counters.complaints} l={`complaints (${rateOf(report.rates.complaintRate, report.counters.sent)} of sent)`} />
              <Kpi n={report.counters.unsubscribes} l="unsubscribes" />
              {/* Kept next to the delivery counters rather than hidden behind a
                  zero check: `rejects` is SES refusing the message outright, and
                  `renderingFailures` means a merge tag did not resolve — the one
                  counter on this screen pointing at OUR bug rather than a
                  recipient's mailbox. A row that only appears when it is nonzero
                  is a row nobody knows to look for. */}
              <Kpi n={report.counters.rejects} l="rejects" />
              <Kpi n={report.counters.renderingFailures} l="rendering failures" />
              <Kpi n={report.counters.deliveryDelays} l="delivery delays" />
            </div>
            <AutoOpensNote />
          </div>
          <div className="card">
            <div className="muted" style={{ marginBottom: 8 }}>Click overlay — editorial links</div>
            <table>
              <thead><tr><th>Link</th><th>Clicks</th><th>Unique</th><th></th></tr></thead>
              <tbody>
                {report.clickMap.rows.map((r) => (
                  <tr key={r.linkId}>
                    <td>{r.label}</td>
                    <td>{r.clicks}</td>
                    <td>{r.unique}</td>
                    <td style={{ width: "40%" }}><div className="bar" style={{ width: `${(r.clicks / maxClicks) * 100}%` }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <h2 style={{ marginTop: 28 }}>Recurring series</h2>
      <p className="muted" style={{ marginTop: -8 }}>
        {/* The distinction that matters: a series is not one campaign. Each
            firing is its own edition with its own counters, so a bad send shows
            up instead of being hidden in a total. */}
        Every edition of a series is reported separately. Pick a window to total
        them.
      </p>
      <div className="card row">
        <label htmlFor="series-picker" style={{ margin: 0 }}>Series</label>
        <select
          id="series-picker"
          value={seriesId}
          onChange={(e) => { setSeriesId(e.target.value); setSeriesReport(null); }}
        >
          <option value="">Choose a series…</option>
          {(series.data ?? []).map((s) => (
            <option key={s.seriesId} value={s.seriesId}>{s.name} ({s.seriesId})</option>
          ))}
        </select>
        <button className="btn" onClick={() => void loadSeries()} disabled={!seriesId}>Load editions</button>
      </div>
      {/* An empty picker read as broken. A series appears here only once it has
          a registry row, which a recurring schedule gets when it is saved (#314);
          schedules from before that have none, and their editions carry no
          seriesId, so the report could not total them anyway. */}
      {series.error && <p className="err">Couldn't load series: {series.error}</p>}
      {!series.loading && !series.error && (series.data ?? []).length === 0 && (
        <p className="muted">
          No recurring series yet. A series is listed here once a recurring
          schedule is saved in Compose &amp; schedule. Recurring schedules created
          before 23 Sep 2026 are not listed.
        </p>
      )}

      {seriesReport?.launchProblem && <p className="err">Last launch {seriesReport.launchProblem.status}: {seriesReport.launchProblem.message}</p>}
      {seriesReport && (
        <>
          <div className="card row" style={{ alignItems: "center", gap: 12 }}>
            {/* Explicitly associated: a bare <label> next to an <input> reads
                as unlabelled to a screen reader, and the two date fields are
                indistinguishable without it. */}
            <label htmlFor="series-from" style={{ margin: 0 }}>From</label>
            <input
              id="series-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <label htmlFor="series-through" style={{ margin: 0 }}>Through</label>
            <input
              id="series-through"
              type="date"
              value={through}
              onChange={(e) => setThrough(e.target.value)}
            />
            {(from || through) && (
              <button className="btn ghost" onClick={() => { setFrom(""); setThrough(""); }}>Clear</button>
            )}
            <span className="muted">
              {editionsInRange.length} of {seriesReport.editions.length} edition
              {seriesReport.editions.length === 1 ? "" : "s"}
            </span>
          </div>

          {editionsInRange.length === 0 ? (
            <div className="card muted">No editions in this window.</div>
          ) : (
            <>
              <div className="card">
                <div className="muted" style={{ marginBottom: 8 }}>
                  Totals for the window. The open and click rates are the average of each
                  edition's own rate (its opens or clicks ÷ its delivered); editions with
                  nothing delivered are left out. The bounce rate is of sent.
                </div>
                <div className="kpis">
                  <Kpi n={rangeTotals.sent ?? 0} l="sent" />
                  <Kpi n={rangeTotals.delivered ?? 0} l="delivered" />
                  <Kpi n={rangeTotals.opens ?? 0} l={`opens (avg ${meanRate(rangeRates?.openRate)})`} />
                  <Kpi n={rangeTotals.clicks ?? 0} l={`clicks (avg ${meanRate(rangeRates?.clickRate)})`} />
                  <Kpi n={rangeTotals.bounces ?? 0} l={`bounces (${bounceRate} of sent)`} />
                  <Kpi n={rangeTotals.complaints ?? 0} l="complaints" />
                  <Kpi n={rangeTotals.unsubscribes ?? 0} l="unsubscribes" />
                </div>
                {/* Covers the Opens column in the editions table below too. */}
                <AutoOpensNote />
              </div>

              <div className="card">
                <div className="muted" style={{ marginBottom: 8 }}>Editions, newest first</div>
                <table>
                  <thead>
                    <tr>
                      <th>Edition</th><th>Subject</th><th>Correction</th><th>Sent</th><th>Delivered</th>
                      <th>Opens</th><th>Clicks</th><th>Bounces</th><th>Unsubs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editionsInRange.map((e) => (
                      <tr key={e.campaignId}>
                        <td className="t-strong">{e.campaignId.slice(seriesId.length + 1) || e.campaignId}</td>
                        <td>{e.subject}{e.correctionOf && <p className="muted">Correction of {e.correctionOf}</p>}{e.warnings?.map((w,i) => <p className="err" key={i}>{w}</p>)}</td><td>{can(grant, "campaigns:schedule", org) && <NewsletterSend org={org} seriesId={seriesId} correctionOf={e.campaignId} />}</td>
                        <td className="num">{e.counters.sent}</td>
                        <td className="num">{e.counters.delivered}</td>
                        <td className="num">{e.counters.opens}</td>
                        <td className="num">{e.counters.clicks}</td>
                        <td className="num">{e.counters.bounces}</td>
                        <td className="num">{e.counters.unsubscribes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {!can(grant, "reports:view", org) && <p className="muted">Your role can't view reports.</p>}
    </div>
  );
}

const pct = (x: number) => `${(x * 100).toFixed(1)}%`;
/** A rate, or "—" when its denominator is zero (no rate, not 0%). */
const rateOf = (rate: number, base: number) => (base > 0 ? pct(rate) : "—");
